import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';
import Icon from '../../components/base/Icon';

const QUOTE_FORM_URL = 'https://readdy.ai/api/form/d6nmvr2qoe30lj0v8rh0';

const SERVICE_OPTIONS = [
  { value: '', label: 'Select a service...' },
  { value: 'Fence Installation & Repair', label: 'Fence Installation & Repair' },
  { value: 'Handyman Services', label: 'Handyman Services' },
  { value: 'Home Remodels', label: 'Home Remodels' },
  { value: 'Other', label: 'Other' },
];

interface QuoteFormState {
  full_name: string;
  phone: string;
  email: string;
  service_needed: string;
  project_details: string;
}

const emptyQuoteForm = (): QuoteFormState => ({
  full_name: '',
  phone: '',
  email: '',
  service_needed: '',
  project_details: '',
});

interface QuoteFormProps {
  formId: string;
  layout?: 'stacked' | 'grid';
}

function QuoteForm({ formId, layout = 'stacked' }: QuoteFormProps) {
  const [values, setValues] = useState<QuoteFormState>(emptyQuoteForm());
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validate = (): string => {
    if (!values.full_name.trim()) return 'Full name is required.';
    if (!values.phone.trim()) return 'Phone number is required.';
    if (!values.email.trim()) return 'Email address is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) return 'Please enter a valid email address.';
    if (!values.service_needed) return 'Please select a service.';
    if (!values.project_details.trim()) return 'Please describe your project.';
    return '';
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setErrorMsg(validationError);
      setStatus('error');
      return;
    }
    setStatus('submitting');
    setErrorMsg('');
    try {
      const params = new URLSearchParams();
      (Object.keys(values) as (keyof QuoteFormState)[]).forEach((key) => {
        params.append(key, values[key]);
      });
      const res = await fetch(QUOTE_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (!res.ok) throw new Error('Submission failed');
      setStatus('success');
      setValues(emptyQuoteForm());
    } catch {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again or call us directly at (208) 204-6331.');
    }
  };

  const inputClass = 'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B45309] focus:border-transparent text-sm';

  return (
    <form onSubmit={handleSubmit} data-readdy-form id={formId} noValidate>
      {status === 'success' && (
        <div className="mb-5 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm flex items-start gap-2" role="alert">
          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name="check" className="w-4 h-4" /></span>
          <span>Thanks — we received your quote request and will contact you within 24 hours.</span>
        </div>
      )}
      {status === 'error' && errorMsg && (
        <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2" role="alert">
          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name="circle-exclamation" className="w-4 h-4" /></span>
          <span>{errorMsg}</span>
        </div>
      )}

      {layout === 'grid' ? (
        <>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor={`${formId}-full_name`} className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
              <input type="text" id={`${formId}-full_name`} name="full_name" required value={values.full_name} onChange={handleChange} className={inputClass} placeholder="John Smith" />
            </div>
            <div>
              <label htmlFor={`${formId}-phone`} className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
              <input type="tel" id={`${formId}-phone`} name="phone" required value={values.phone} onChange={handleChange} className={inputClass} placeholder="(208) 555-0123" />
            </div>
          </div>
          <div className="mb-4">
            <label htmlFor={`${formId}-email`} className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
            <input type="email" id={`${formId}-email`} name="email" required value={values.email} onChange={handleChange} className={inputClass} placeholder="john@example.com" />
          </div>
          <div className="mb-4">
            <label htmlFor={`${formId}-service_needed`} className="block text-sm font-medium text-gray-700 mb-1">Service Needed *</label>
            <select id={`${formId}-service_needed`} name="service_needed" required value={values.service_needed} onChange={handleChange} className={`${inputClass} cursor-pointer`}>
              {SERVICE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="mb-6">
            <label htmlFor={`${formId}-project_details`} className="block text-sm font-medium text-gray-700 mb-1">Project Details *</label>
            <textarea id={`${formId}-project_details`} name="project_details" required rows={3} maxLength={500} value={values.project_details} onChange={handleChange} className={`${inputClass} resize-none`} placeholder="Describe what you need help with..." />
            <p className="text-xs text-gray-500 mt-1">{values.project_details.length}/500 characters</p>
          </div>
          <button type="submit" disabled={status === 'submitting'} className="w-full bg-[#B45309] hover:bg-[#92400E] disabled:opacity-60 text-white px-6 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2">
            {status === 'submitting' ? 'Sending...' : 'Request Free Quote'}
            <Icon name="arrow-right" className="w-4 h-4" />
          </button>
        </>
      ) : (
        <div className="space-y-4">
          <div>
            <label htmlFor={`${formId}-full_name`} className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
            <input type="text" id={`${formId}-full_name`} name="full_name" required value={values.full_name} onChange={handleChange} className={inputClass} placeholder="John Smith" />
          </div>
          <div>
            <label htmlFor={`${formId}-phone`} className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
            <input type="tel" id={`${formId}-phone`} name="phone" required value={values.phone} onChange={handleChange} className={inputClass} placeholder="(208) 555-0123" />
          </div>
          <div>
            <label htmlFor={`${formId}-email`} className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
            <input type="email" id={`${formId}-email`} name="email" required value={values.email} onChange={handleChange} className={inputClass} placeholder="john@example.com" />
          </div>
          <div>
            <label htmlFor={`${formId}-service_needed`} className="block text-sm font-medium text-gray-700 mb-1">Service Needed *</label>
            <select id={`${formId}-service_needed`} name="service_needed" required value={values.service_needed} onChange={handleChange} className={`${inputClass} cursor-pointer`}>
              {SERVICE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor={`${formId}-project_details`} className="block text-sm font-medium text-gray-700 mb-1">Project Details *</label>
            <textarea id={`${formId}-project_details`} name="project_details" required rows={4} maxLength={500} value={values.project_details} onChange={handleChange} className={`${inputClass} resize-none`} placeholder="Describe what you need help with..." />
            <p className="text-xs text-gray-500 mt-1">{values.project_details.length}/500 characters</p>
          </div>
          <button type="submit" disabled={status === 'submitting'} className="w-full bg-[#B45309] hover:bg-[#92400E] disabled:opacity-60 text-white px-6 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer">
            {status === 'submitting' ? 'Sending...' : 'Submit Request'}
          </button>
        </div>
      )}
    </form>
  );
}

export default function HomePage() {
  const [showReviewSuccess, setShowReviewSuccess] = useState(false);
  const [reviewPhotos, setReviewPhotos] = useState<string[]>([]);
  const [reviewName, setReviewName] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [hoveredStar, setHoveredStar] = useState(0);

  useSEO({
    title: 'Boise Fence, Handyman & Remodel Experts',
    description: 'Top Boise Fence Installation & Repair, Handyman Services, and Local Home Remodels. Licensed, insured, and trusted in Treasure Valley. Call or Get a Free Quote Today!',
    keywords: 'Boise fence installation, fence repair Boise, handyman services Boise, home remodels Boise, kitchen remodeling Meridian, bathroom renovation Nampa',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com'}/`,
    schema: {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Fix Right Services",
      "description": "Boise's trusted experts for fence installation, handyman services, and home remodels. Licensed, insured, and serving the entire Treasure Valley.",
      "url": import.meta.env.VITE_SITE_URL || "https://fixrightservices.com",
      "telephone": "+12082046331",
      "email": "Southernkole@gmail.com",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Boise",
        "addressRegion": "ID",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "43.6150",
        "longitude": "-116.2023"
      },
      "hasMap": "https://maps.google.com/?q=Boise,ID",
      "priceRange": "$$",
      "currenciesAccepted": "USD",
      "paymentAccepted": "Cash, Check, Credit Card",
      "areaServed": [
        { "@type": "City", "name": "Boise", "sameAs": "https://en.wikipedia.org/wiki/Boise,_Idaho" },
        { "@type": "City", "name": "Meridian", "sameAs": "https://en.wikipedia.org/wiki/Meridian,_Idaho" },
        { "@type": "City", "name": "Nampa", "sameAs": "https://en.wikipedia.org/wiki/Nampa,_Idaho" },
        { "@type": "City", "name": "Caldwell", "sameAs": "https://en.wikipedia.org/wiki/Caldwell,_Idaho" },
        { "@type": "City", "name": "Eagle" },
        { "@type": "City", "name": "Star" },
        { "@type": "City", "name": "Kuna" },
        { "@type": "City", "name": "Middleton" },
        { "@type": "City", "name": "Garden City" },
        { "@type": "City", "name": "Treasure Valley" }
      ],
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": "43.6150",
          "longitude": "-116.2023"
        },
        "geoRadius": "50000"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Home Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Fence Installation & Repair",
              "description": "Custom fence building, repairs, gate installation, and maintenance. Wood, vinyl, chain link, and composite materials."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Handyman Services",
              "description": "General repairs and maintenance, fixture installation, drywall repair and painting, small home improvement jobs."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Home Remodels",
              "description": "Kitchen remodeling, bathroom renovations, basement finishing, and interior upgrades."
            }
          }
        ]
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "08:00",
          "closes": "18:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": "Saturday",
          "opens": "09:00",
          "closes": "15:00"
        }
      ],
      "sameAs": [
        "https://www.facebook.com/profile.php?id=61582658047880",
        "https://www.instagram.com/fixright_services/"
      ],
      "knowsAbout": [
        "Fence Installation",
        "Fence Repair",
        "Handyman Services",
        "Home Remodeling",
        "Bathroom Renovation",
        "Kitchen Remodeling",
        "Basement Finishing",
        "Drywall Repair"
      ],
      "slogan": "Licensed, Insured & Trusted in Treasure Valley",
      "foundingLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Boise",
          "addressRegion": "ID",
          "addressCountry": "US"
        }
      },
      "employee": {
        "@type": "Person",
        "name": "Kole Southern",
        "jobTitle": "Owner & Lead Contractor"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "5",
        "bestRating": "5",
        "worstRating": "1",
        "reviewCount": "3"
      },
      "review": [
        {
          "@type": "Review",
          "author": { "@type": "Person", "name": "Maralee Ainsworth" },
          "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
          "reviewBody": "Kole is skilled, reliable, and takes great pride in his work. He consistently arrived on time, communicated clearly, and followed through exactly as promised. His workmanship is excellent and the results have always been durable, clean, and well-finished.",
          "publisher": { "@type": "Organization", "name": "Fix Right Services" }
        },
        {
          "@type": "Review",
          "author": { "@type": "Person", "name": "Julie Masner" },
          "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
          "reviewBody": "I highly recommend Kole Southern of Fix Right Services! He's professional, skilled and keeps you informed of the project needs and progress. He's prompt and very honest and reliable. I was very happy with the results of his work.",
          "publisher": { "@type": "Organization", "name": "Fix Right Services" }
        },
        {
          "@type": "Review",
          "author": { "@type": "Person", "name": "Maralee Ainsworth" },
          "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
          "reviewBody": "I am pleased to write this letter of recommendation for Kole Southern of Fix Right Services, who has provided outstanding handyman services for me on multiple occasions. He is reliable, punctual, and always follows through on his commitments.",
          "publisher": { "@type": "Organization", "name": "Fix Right Services" }
        }
      ]
    }
  });

  const handleReviewSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    try {
      await fetch('https://readdy.ai/api/form/d6fnkigr225iqdhs0dog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString(),
      });
      setShowReviewSuccess(true);
      form.reset();
      setReviewName('');
      setReviewRating(5);
      setReviewText('');
      setReviewPhotos([]);
      setTimeout(() => setShowReviewSuccess(false), 5000);
    } catch (error) {
      console.error('Review submission error:', error);
    }
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    Array.from(files).slice(0, 3 - reviewPhotos.length).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        if (ev.target?.result) {
          setReviewPhotos((prev) => [...prev, ev.target!.result as string].slice(0, 3));
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index: number) => {
    setReviewPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-20">
            <h1 className="flex-shrink-0">
              <a href="/">
                <img
                  src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/2ea97a783d40b0afc97fadf5f7236f78.webp"
                  alt="Fix Right Services Logo"
                  width="180"
                  height="56"
                  className="h-14 w-auto"
                  loading="eager"
                />
              </a>
            </h1>
            <div className="hidden md:flex items-center gap-6 text-sm font-medium text-gray-700 ml-8">
              <Link to="/" className="text-[#B45309] font-semibold cursor-pointer">Home</Link>
              <Link to="/services" className="hover:text-[#B45309] transition-colors cursor-pointer">Services</Link>
              <Link to="/about" className="hover:text-[#B45309] transition-colors cursor-pointer">About</Link>
              <Link to="/blog" className="hover:text-[#B45309] transition-colors cursor-pointer">Blog</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden" aria-label="Hero">
        <div className="absolute inset-0 z-0">
          <picture>
            <source
              type="image/webp"
              srcSet="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/f086650a51169a68d329ee08f6e6d8f4.webp 480w,
                      https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/f086650a51169a68d329ee08f6e6d8f4.webp 640w,
                      https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/f086650a51169a68d329ee08f6e6d8f4.webp 960w,
                      https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/f086650a51169a68d329ee08f6e6d8f4.webp 1280w,
                      https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/f086650a51169a68d329ee08f6e6d8f4.webp 1600w"
              sizes="100vw"
            />
            <img
              src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/f086650a51169a68d329ee08f6e6d8f4.webp"
              alt="Professional Home Services"
              width="1920"
              height="1080"
              className="w-full h-full object-cover object-top"
              fetchpriority="high"
              loading="eager"
              decoding="async"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Fix Right Services — Boise&apos;s Fence, Handyman &amp; Remodel Experts!
              </h2>
              <p className="text-xl sm:text-2xl mb-8 text-white/95">
                Licensed, Insured &amp; Trusted in Treasure Valley. Fast Response, Quality Work, Fair Prices.
              </p>
              <div className="flex flex-wrap gap-4 mb-8">
                <a
                  href="tel:+12082046331"
                  className="inline-flex items-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-8 py-3 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4" /></span>
                  Call Now
                </a>
                <a
                  href="#quote-form"
                  className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-900 px-8 py-3 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center"><Icon name="file-lines" className="w-4 h-4" /></span>
                  Get Your Free Quote
                </a>
              </div>
              <div className="flex flex-wrap gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center"><Icon name="location-dot" className="w-4 h-4 text-[#F59E0B]" /></span>
                  <span>Serving Boise, Meridian, Nampa &amp; Caldwell</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center"><Icon name="clock" className="w-4 h-4 text-[#F59E0B]" /></span>
                  <span>Fast Response Times</span>
                </div>
              </div>
            </div>

            {/* Hero Quote Form */}
            <div className="bg-white rounded-2xl shadow-2xl p-8" id="quote-form">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Get Your Free Quote</h3>
              <p className="text-gray-600 mb-6">Fill out the form and we&apos;ll get back to you within 24 hours</p>
              <QuoteForm formId="hero-quote-form" layout="stacked" />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" aria-label="Why choose us">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Why Boise Homeowners Trust Fix Right Services
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We&apos;re your local Treasure Valley experts, committed to delivering exceptional service on every project. From small repairs to complete remodels, we treat your home like our own.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-[#B45309]/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name="shield-halved" className="w-7 h-7 text-[#B45309]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Licensed &amp; Insured</h3>
              <p className="text-gray-600">Fully licensed and insured for your peace of mind and protection.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-[#B45309]/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name="clock" className="w-7 h-7 text-[#B45309]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Fast Response</h3>
              <p className="text-gray-600">Quick turnaround times and flexible scheduling to fit your needs.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-[#B45309]/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name="star" className="w-7 h-7 text-[#B45309]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Quality Work</h3>
              <p className="text-gray-600">Exceptional craftsmanship and attention to detail on every job.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-[#B45309]/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name="location-dot" className="w-7 h-7 text-[#B45309]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Local Experts</h3>
              <p className="text-gray-600">Proudly serving Boise and the entire Treasure Valley area.</p>
            </div>
          </div>

          {/* Reviews Section */}
          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-2">What Our Customers Say</h3>
            <p className="text-gray-500 text-center mb-8 text-sm">Real reviews from real Treasure Valley homeowners</p>

            <div className="grid md:grid-cols-3 gap-6 mb-10">
              <div className="border border-gray-100 rounded-xl p-5 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full flex-shrink-0 bg-[#B45309]/10 flex items-center justify-center">
                    <Icon name="star" className="w-6 h-6 text-[#B45309]" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Maralee Ainsworth</p>
                    <p className="text-xs text-gray-500">Caldwell, ID</p>
                  </div>
                </div>
                <div className="flex gap-0.5 mb-3">
                  <span className="sr-only">Rated 5 out of 5 stars</span>
                  {[...Array(5)].map((_, i) => (
                    <Icon key={i} name="star" className="w-4 h-4 text-[#B45309]" ariaHidden={true} />
                  ))}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  &quot;Kole is skilled, reliable, and takes great pride in his work. He consistently arrived on time, communicated clearly, and followed through exactly as promised. His workmanship is excellent and the results have always been durable, clean, and well-finished.&quot;
                </p>
              </div>

              <div className="border border-gray-100 rounded-xl p-5 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full flex-shrink-0 bg-[#B45309]/10 flex items-center justify-center">
                    <Icon name="star" className="w-6 h-6 text-[#B45309]" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Julie Masner</p>
                    <p className="text-xs text-gray-500">Beacon Light</p>
                  </div>
                </div>
                <div className="flex gap-0.5 mb-3">
                  <span className="sr-only">Rated 5 out of 5 stars</span>
                  {[...Array(5)].map((_, i) => (
                    <Icon key={i} name="star" className="w-4 h-4 text-[#B45309]" ariaHidden={true} />
                  ))}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  &quot;I highly recommend Kole Southern of Fix Right Services! He&apos;s professional, skilled and keeps you informed of the project needs and progress. He&apos;s prompt and very honest and reliable. I was very happy with the results of his work.&quot;
                </p>
              </div>

              <div className="border border-gray-100 rounded-xl p-5 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full flex-shrink-0 bg-[#B45309]/10 flex items-center justify-center">
                    <Icon name="star" className="w-6 h-6 text-[#B45309]" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Maralee Ainsworth</p>
                    <p className="text-xs text-gray-500">Passero Ridge</p>
                  </div>
                </div>
                <div className="flex gap-0.5 mb-3">
                  <span className="sr-only">Rated 5 out of 5 stars</span>
                  {[...Array(5)].map((_, i) => (
                    <Icon key={i} name="star" className="w-4 h-4 text-[#B45309]" ariaHidden={true} />
                  ))}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  &quot;I am pleased to write this letter of recommendation for Kole Southern of Fix Right Services, who has provided outstanding handyman services for me on multiple occasions. He is reliable, punctual, and always follows through on his commitments.&quot;
                </p>
              </div>
            </div>

            {/* Leave a Review Form */}
            <div id="leave-review" className="border-t border-gray-100 pt-8">
              <h3 className="text-xl font-bold text-gray-900 mb-1">Leave Us a Review</h3>
              <p className="text-gray-500 text-sm mb-6">Share your experience — it helps other Boise homeowners find trusted help.</p>

              {showReviewSuccess && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm" role="alert">
                  <Icon name="check" className="w-4 h-4 inline-block mr-2" />
                  Thank you for your review! We really appreciate it.
                </div>
              )}

              <form onSubmit={handleReviewSubmit} data-readdy-form id="leave-review-form">
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="review-name" className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
                    <input type="text" id="review-name" name="name" required value={reviewName} onChange={(e) => setReviewName(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B45309] focus:border-transparent text-sm" placeholder="Jane Smith" />
                  </div>
                  <div>
                    <label htmlFor="review-location" className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input type="text" id="review-location" name="location" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B45309] focus:border-transparent text-sm" placeholder="Boise, ID" />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2" id="rating-label">Rating *</label>
                  <div className="flex gap-1" role="group" aria-labelledby="rating-label">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                        onClick={() => setReviewRating(star)}
                        onMouseEnter={() => setHoveredStar(star)}
                        onMouseLeave={() => setHoveredStar(0)}
                        className="w-9 h-9 flex items-center justify-center cursor-pointer transition-transform hover:scale-110"
                      >
                        <Icon
                          name={(hoveredStar || reviewRating) >= star ? 'star' : 'star-regular'}
                          className={`w-6 h-6 ${(hoveredStar || reviewRating) >= star ? 'text-[#B45309]' : 'text-gray-300'}`}
                        />
                      </button>
                    ))}
                  </div>
                  <input type="hidden" name="rating" value={reviewRating} />
                </div>

                <div className="mb-4">
                  <label htmlFor="review-service" className="block text-sm font-medium text-gray-700 mb-1">Service Used</label>
                  <select id="review-service" name="service" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B45309] focus:border-transparent text-sm cursor-pointer">
                    <option value="">Select a service...</option>
                    <option value="Fence Installation & Repair">Fence Installation &amp; Repair</option>
                    <option value="Handyman Services">Handyman Services</option>
                    <option value="Home Remodels">Home Remodels</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label htmlFor="review-text" className="block text-sm font-medium text-gray-700 mb-1">Your Review *</label>
                  <textarea id="review-text" name="review" required rows={4} maxLength={500} value={reviewText} onChange={(e) => setReviewText(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B45309] focus:border-transparent text-sm resize-none" placeholder="Tell us about your experience with Fix Right Services..."></textarea>
                  <p className="text-xs text-gray-600 mt-1">{reviewText.length}/500 characters</p>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Add Photos <span className="text-gray-600 font-normal">(optional, up to 3)</span>
                  </label>
                  <div className="flex gap-3 flex-wrap">
                    {reviewPhotos.map((photo, idx) => (
                      <div key={idx} className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200">
                        <img src={photo} alt={`Review photo ${idx + 1}`} width="96" height="96" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          aria-label={`Remove photo ${idx + 1}`}
                          onClick={() => removePhoto(idx)}
                          className="absolute top-1 right-1 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center cursor-pointer hover:bg-black/80 transition-colors"
                        >
                          <Icon name="xmark" className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    ))}
                    {reviewPhotos.length < 3 && (
                      <label className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#B45309] transition-colors">
                        <Icon name="camera" className="w-6 h-6 text-gray-400" />
                        <span className="text-xs text-gray-600 mt-1">Add Photo</span>
                        <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} name="photos" />
                      </label>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 mt-2">Photos will show as &quot;Uncollectable&quot; in submission</p>
                </div>

                <button type="submit" className="bg-[#B45309] hover:bg-[#92400E] text-white px-8 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2">
                  <Icon name="paper-plane" className="w-4 h-4" />
                  Submit Review
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" aria-label="Our services">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-lg text-gray-600">Professional home services tailored to your needs</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow">
              <div className="h-64 overflow-hidden">
                <picture>
                  <source type="image/webp" srcSet="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/76826b84de881855eb799ff9c94ff780.webp 480w, https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/76826b84de881855eb799ff9c94ff780.webp 640w, https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/76826b84de881855eb799ff9c94ff780.webp 960w" sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw" />
                  <img src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/76826b84de881855eb799ff9c94ff780.webp" alt="Fence Installation Services" width="600" height="400" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" loading="lazy" decoding="async" />
                </picture>
              </div>
              <div className="p-8">
                <div className="w-12 h-12 bg-[#B45309]/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name="house-chimney" className="w-6 h-6 text-[#B45309]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Fence Installation &amp; Repair</h3>
                <p className="text-gray-600 mb-6">
                  Custom fence building, repairs, gate installation, and maintenance. We work with wood, vinyl, chain link, and composite materials to create the perfect fence for your property.
                </p>
                <a href="tel:+12082046331" className="inline-flex items-center gap-2 text-[#B45309] hover:text-[#92400E] font-semibold cursor-pointer">
                  Get a Fence Quote
                  <Icon name="arrow-right" className="w-4 h-4" />
                </a>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow">
              <div className="h-64 overflow-hidden">
                <picture>
                  <source type="image/webp" srcSet="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/533edc55cfb7863ec611a1af13fe4de5.webp 480w, https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/533edc55cfb7863ec611a1af13fe4de5.webp 640w, https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/533edc55cfb7863ec611a1af13fe4de5.webp 960w" sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw" />
                  <img src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/533edc55cfb7863ec611a1af13fe4de5.webp" alt="Handyman Services" width="600" height="400" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" loading="lazy" decoding="async" />
                </picture>
              </div>
              <div className="p-8">
                <div className="w-12 h-12 bg-[#B45309]/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name="screwdriver-wrench" className="w-6 h-6 text-[#B45309]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Handyman Services</h3>
                <ul className="text-gray-600 mb-6 space-y-2">
                  <li className="flex items-start gap-2">
                    <Icon name="check" className="w-4 h-4 text-[#B45309] mt-1 flex-shrink-0" />
                    <span>General repairs and maintenance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="check" className="w-4 h-4 text-[#B45309] mt-1 flex-shrink-0" />
                    <span>Fixture installation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="check" className="w-4 h-4 text-[#B45309] mt-1 flex-shrink-0" />
                    <span>Drywall repair and painting</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="check" className="w-4 h-4 text-[#B45309] mt-1 flex-shrink-0" />
                    <span>Small home improvement jobs</span>
                  </li>
                </ul>
                <a href="tel:+12082046331" className="inline-flex items-center gap-2 text-[#B45309] hover:text-[#92400E] font-semibold cursor-pointer">
                  Schedule Service
                  <Icon name="arrow-right" className="w-4 h-4" />
                </a>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow">
              <div className="h-64 overflow-hidden">
                <picture>
                  <source type="image/webp" srcSet="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/ebb4300033bdd705737722dc14cda213.webp 480w, https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/ebb4300033bdd705737722dc14cda213.webp 640w, https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/ebb4300033bdd705737722dc14cda213.webp 960w" sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw" />
                  <img src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/ebb4300033bdd705737722dc14cda213.webp" alt="Home Remodeling Services" width="600" height="400" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" loading="lazy" decoding="async" />
                </picture>
              </div>
              <div className="p-8">
                <div className="w-12 h-12 bg-[#B45309]/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name="paintbrush" className="w-6 h-6 text-[#B45309]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Home Remodels</h3>
                <ul className="text-gray-600 mb-6 space-y-2">
                  <li className="flex items-start gap-2">
                    <Icon name="check" className="w-4 h-4 text-[#B45309] mt-1 flex-shrink-0" />
                    <span>Kitchen remodeling</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="check" className="w-4 h-4 text-[#B45309] mt-1 flex-shrink-0" />
                    <span>Bathroom renovations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="check" className="w-4 h-4 text-[#B45309] mt-1 flex-shrink-0" />
                    <span>Basement finishing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="check" className="w-4 h-4 text-[#B45309] mt-1 flex-shrink-0" />
                    <span>Interior upgrades and transformations</span>
                  </li>
                </ul>
                <a href="tel:+12082046331" className="inline-flex items-center gap-2 text-[#B45309] hover:text-[#92400E] font-semibold cursor-pointer">
                  Start Your Remodel
                  <Icon name="arrow-right" className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Secondary CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#B45309]" aria-label="Call to action" id="contact">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Ready to Get Started?</h2>
            <p className="text-xl text-white/95 mb-8">Contact us today for a free quote on your fence, handyman, or remodeling project</p>
            <a
              href="tel:+12082046331"
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-[#B45309] px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4" /></span>
              Call (208) 204-6331 Now
            </a>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-xl max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">Or Request a Quote Online</h3>
            <p className="text-gray-600 mb-6 text-center">We&apos;ll respond within 24 hours</p>
            <QuoteForm formId="secondary-quote-form" layout="grid" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/2ea97a783d40b0afc97fadf5f7236f78.webp" alt="Fix Right Services" width="180" height="48" className="h-12 w-auto mb-4" loading="lazy" />
              <p className="text-gray-400 text-sm">
                Boise&apos;s trusted experts for fence installation, handyman services, and home remodels.
              </p>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Services</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>Fence Installation &amp; Repair</li>
                <li>Handyman Services</li>
                <li>Home Remodels</li>
                <li>Kitchen Renovations</li>
                <li>Bathroom Remodeling</li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Resources</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/services" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Our Services</Link>
                </li>
                <li>
                  <Link to="/blog" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Tips &amp; Advice</Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Contact</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5"><Icon name="phone" className="w-3.5 h-3.5 text-[#F59E0B]" /></span>
                  <a href="tel:+12082046331" className="hover:text-white transition-colors cursor-pointer">(208) 204-6331</a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5"><Icon name="envelope" className="w-3.5 h-3.5 text-[#F59E0B]" /></span>
                  <a href="mailto:Southernkole@gmail.com" className="hover:text-white transition-colors cursor-pointer">Southernkole@gmail.com</a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5"><Icon name="location-dot" className="w-3.5 h-3.5 text-[#F59E0B]" /></span>
                  <span>Serving Boise, Meridian, Nampa &amp; Caldwell</span>
                </li>
              </ul>
              <div className="flex gap-3 mt-4">
                <a href="https://www.facebook.com/profile.php?id=61582658047880" aria-label="Visit our Facebook page" rel="nofollow noopener noreferrer" target="_blank" className="w-10 h-10 bg-gray-800 hover:bg-[#B45309] rounded-lg flex items-center justify-center transition-colors cursor-pointer">
                  <Icon name="facebook" className="w-5 h-5" />
                </a>
                <a href="https://www.instagram.com/fixright_services/" aria-label="Visit our Instagram page" rel="nofollow noopener noreferrer" target="_blank" className="w-10 h-10 bg-gray-800 hover:bg-[#B45309] rounded-lg flex items-center justify-center transition-colors cursor-pointer">
                  <Icon name="instagram" className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <p>&copy; 2025 Fix Right Services. All rights reserved.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">
              Website Builder
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
